import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { MobileNav } from './components/mobile-nav.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SearchBar } from './components/search-bar.tsx'
import { SiteNav } from './components/site-nav.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { Toaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
		{
			rel: 'preconnect',
			href: 'https://fonts.gstatic.com',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Oswald:wght@600;700&display=swap',
		},
		{ rel: 'icon', href: '/favicon.ico', sizes: '48x48' },
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'icon', type: 'image/png', sizes: '96x96', href: '/favicons/favicon-96x96.png' },
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Suomen frisbeegolfradat | Frisbeegolfradat.fi' : 'Virhe | Frisbeegolfradat.fi' },
		{ name: 'description', content: `Kaikki Suomen frisbeegolfradat yhdessä paikassa` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	const isOnSearchPage = matches.find((m) => m.id === 'routes/users/index')
	// While the site is in preview mode, the marketing index ("/") is the only
	// finished page — strip nav, search, login and footer links so visitors
	// don't hit dead links.
	const isMarketingIndex = matches.some(
		(m) => m.id === 'routes/_marketing/index',
	)
	const searchBar = isOnSearchPage ? null : (
		<SearchBar status="idle" compact />
	)
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="bg-app flex min-h-screen flex-col items-center">
				<div className="bg-background ring-border/40 flex w-full max-w-7xl flex-1 flex-col overflow-hidden shadow-2xl ring-1">
					<div className="bg-primary h-1.5 w-full" aria-hidden="true" />
					<SiteHeader
						user={user}
						searchBar={searchBar}
						minimal={isMarketingIndex}
					/>
					<main className="flex flex-1 flex-col">
						<Outlet />
					</main>
					<SiteFooter
						themePreference={data.requestInfo.userPrefs.theme}
						minimal={isMarketingIndex}
					/>
				</div>
			</div>
			<Toaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function SiteHeader({
	user,
	searchBar,
	minimal = false,
}: {
	user: ReturnType<typeof useOptionalUser>
	searchBar: React.ReactNode
	minimal?: boolean
}) {
	if (minimal) {
		return (
			<header className="border-primary/15 from-primary/8 border-b bg-linear-to-b to-transparent">
				<div className="container py-3 md:py-4">
					<div className="flex items-center">
						<Logo />
					</div>
				</div>
			</header>
		)
	}
	return (
		<header className="border-primary/15 from-primary/8 border-b bg-linear-to-b to-transparent">
			<div className="container py-3 md:py-4">
				<div className="flex items-center gap-3 md:gap-6">
					<Logo />
					<SiteNav />

					{/* Spacer — pushes the right cluster to the edge regardless of
					    which middle items are hidden. */}
					<div className="flex-1" />

					{/* Search: inline from md+ (own row below at <md). */}
					<div className="hidden md:block md:w-48 lg:w-64">
						{searchBar}
					</div>

					{/* Right cluster: login + (mobile) hamburger. */}
					<div className="flex items-center gap-2">
						{user ? (
							<UserDropdown />
						) : (
							<LoginButtons />
						)}
						<MobileNav />
					</div>
				</div>

				{/* Search: own row at <md. */}
				<div className="mt-3 block md:hidden">{searchBar}</div>
			</div>
		</header>
	)
}

function LoginButtons() {
	return (
		<Button asChild variant="default" size="sm">
			<Link to="/login">Kirjaudu</Link>
		</Button>
	)
}

function SiteFooter({
	themePreference,
	minimal = false,
}: {
	themePreference: Theme | null
	minimal?: boolean
}) {
	if (minimal) {
		return (
			<footer className="border-primary/30 bg-muted/50 border-t-2">
				<div className="container flex flex-col items-center gap-3 py-8 text-center">
					<Logo />
					<p className="text-muted-foreground max-w-sm text-sm">
						Esikatselu uudistuvasta frisbeegolfradat.fi:stä. Palaute:{' '}
						<a
							href="mailto:info@fgr.fi"
							className="text-primary hover:underline"
						>
							info@fgr.fi
						</a>
					</p>
				</div>
				<div className="border-border/60 border-t">
					<div className="container flex items-center justify-between py-4 text-xs">
						<span className="text-muted-foreground">
							© {new Date().getFullYear()} frisbeegolfradat.fi
						</span>
						<ThemeSwitch userPreference={themePreference} />
					</div>
				</div>
			</footer>
		)
	}
	return (
		<footer className="border-primary/30 bg-muted/50 mt-16 border-t-2">
			<div className="container grid gap-8 py-10 md:grid-cols-4">
				<div className="md:col-span-2">
					<Logo />
					<p className="text-muted-foreground mt-3 max-w-sm text-sm">
						Kaikki Suomen frisbeegolfradat yhdessä paikassa.
					</p>
				</div>
				<div>
					<h3 className="text-sm font-semibold">Sivusto</h3>
					<ul className="mt-3 space-y-2 text-sm">
						<li>
							<Link
								to="/sivukartta"
								className="text-muted-foreground hover:text-foreground"
							>
								Sivukartta
							</Link>
						</li>
						<li>
							<Link
								to="/rekisteriseloste"
								className="text-muted-foreground hover:text-foreground"
							>
								Rekisteriseloste
							</Link>
						</li>
					</ul>
				</div>
				<div>
					<h3 className="text-sm font-semibold">Yhteystiedot</h3>
					<ul className="mt-3 space-y-2 text-sm">
						<li>
							<Link
								to="/yhteystiedot"
								className="text-muted-foreground hover:text-foreground"
							>
								Ota yhteyttä
							</Link>
						</li>
						<li>
							<Link
								to="/mediakortti"
								className="text-muted-foreground hover:text-foreground"
							>
								Mediakortti
							</Link>
						</li>
					</ul>
				</div>
			</div>
			<div className="border-border/60 border-t">
				<div className="container flex items-center justify-between py-4 text-xs">
					<span className="text-muted-foreground">
						© {new Date().getFullYear()} frisbeegolfradat.fi
					</span>
					<ThemeSwitch userPreference={themePreference} />
				</div>
			</div>
		</footer>
	)
}

function Logo() {
	return (
		<Link
			to="/"
			aria-label="frisbeegolfradat.fi"
			className="text-primary hover:text-primary/80 dark:text-white dark:hover:text-white/80 inline-flex items-center transition-colors"
		>
			<span
				aria-hidden="true"
				className="block h-7 w-[9.55rem] bg-current mask-[url(/img/logo.svg)] mask-[left_center] mask-no-repeat mask-contain"
			/>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
